import { useTimeEventsOfTheDay } from "@kalyzee/kast-web-sdk";
import { MeetingOutputDTO, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { BoxOverlay, BoxOverlayProps, OverlayContainer } from "@kalyzee/kalyzee-ui";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isToday } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { ErrorView } from "../../../../components/utils/ErrorView";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { addPopup, removePopup } from "../../../../helpers/popup";
import { useSettings } from "../../../../hooks/settings";
import styles from "./list.module.css";

const AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY = 10 * 60 * 1000; // 10 minutes

export const EventsAgenda = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const [date, setDate] = useState<Date>(new Date());

  const user = useAppStoreSelector(state => state.user);
  const lastLeftMeetingId = useAppStoreSelector(state => state.lastLeftMeetingId);

  const [selectedEventId, setSelectedEventId] = useState<string>();
  const selectedEventAtRef = useRef<Date>();
  const [settings] = useSettings();
  const meetingId = pageContextRef.current?.searchParams?.get("meeting");

  const autojoin = pageContextRef.current?.searchParams?.get("autojoin") === "true";
  const canceledAutoJoinIds = useRef<Set<string>>(new Set());
  const mountTime = useRef<number>(Date.now());

  const sdkCtx = useSdkContext();

  const [mode] = useState<"meeting" | "list">(meetingId ? "meeting" : "list");
  const { timeEvents, currentTimeEvents, error, retry } = useTimeEventsOfTheDay(sdkCtx.client, date, user ? { type: "user", id: user._id } : undefined, false, {
    meeting: mode === "meeting" ? meetingId ?? undefined : undefined,
    targets: [TimeEventTargetModels.Meeting],
    populate: { room: true, actor: { identity: true } },
  });
  const currentTimeEvent = useMemo(() => currentTimeEvents.length ? currentTimeEvents[0] : undefined, [currentTimeEvents]);

  const onJoinEvent = (eventId: string) => {
    const selectedEvent = timeEvents?.find((event) => event.id === eventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (selectedMeeting) {
      pageContextRef.current?.goTo(
        PageMasterMode.MEETING_DASHBOARD,
        { id: selectedMeeting._id },
        { queries: { id: undefined } },
      );
    }
  };

  useEffect(() => {
    if (!autojoin) return;

    const allRunning = timeEvents?.filter(
      event => event.target.state === MeetingState.RUNNING,
    );

    // If we just left a meeting, we cancel the events with auto-join to it
    // for 1 minute. Avoids re-joining a meeting we just left.
    if (lastLeftMeetingId && Date.now() - mountTime.current < 60 * 1000) {
      allRunning?.forEach(event => {
        if (event.target.id === lastLeftMeetingId) {
          canceledAutoJoinIds.current.add(event.id);
        }
      });
    }

    const running = allRunning?.find(event =>
      !canceledAutoJoinIds.current.has(event.id)
      && event.target.id !== lastLeftMeetingId,
    );

    if (!running) return;

    let currentPopup: React.ReactNode;
    const buttons: BoxOverlayProps["buttons"] = [
      {
        label: "Rejoindre",
        onClick: () => {
          onJoinEvent(running.id);
        },
        validateAfter: 10,
      },
      {
        label: "Rester sur cette page",
        onClick: () => {
          allRunning?.forEach(event => {
            canceledAutoJoinIds.current.add(event.id);
          });
          removePopup(currentPopup);
        },
      },
    ];

    const meetingName = running.target.name;
    const title = meetingName
      ? <>Le meeting <b>"{meetingName}"</b> est en cours</>
      : <>Un meeting est en cours</>;

    currentPopup = (
      <OverlayContainer
        visible
        style={{
          width: "100vw",
          height: "100vh",
        }}
        overlay={
          <BoxOverlay
            title={title}
            level={"info"}
            buttons={buttons}
          />
        }
      />
    );
    addPopup(currentPopup, "meeting_auto_join", "main");

    return () => {
      removePopup(currentPopup);
    };
  }, [timeEvents, lastLeftMeetingId]);

  useEffect(() => {
    if (!isToday(date)) return;
    const interval = setInterval(() => {
      if (!isToday(date.getDate())) {
        setDate(new Date());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [date]);

  useEffect(() => {
    const now = new Date();
    if (!selectedEventId && currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
    else if (selectedEventAtRef.current) {
      const updateAfter = selectedEventAtRef.current.getTime() + AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY - now.getTime();
      if (updateAfter > 0) {
        const timeout = setTimeout(() => {
          if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
        }, updateAfter);
        return () => {
          clearTimeout(timeout);
        };
      } else {
        if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
      }
    }
  }, [currentTimeEvent, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    selectedEventAtRef.current = new Date();
  }, [selectedEventId]);

  if (error) return <ErrorView retry={() => { retry(); }} />;

  if (mode === "meeting") {
    const timeEvent = timeEvents?.length ? timeEvents[0] : undefined;
    return (
      <div className={styles.eventsList}>
        <TimeEventsAgenda
          timeEvents={timeEvent}
          placeholdersNb={1}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
          onJoinEvent={onJoinEvent}
          kind="student"
          user={user}
        />
      </div>
    );
  }

  return (
    <div className={styles.eventsList}>
      {settings.dev ? (
        <DatePicker
          showIcon
          selected={date}
          date={date ?? undefined}
          onChange={(d) => {
            if (!d) return;
            setDate(d);
          }}
        />
      ) : null}
      <TimeEventsAgenda
        timeEvents={timeEvents ? timeEvents.filter((event) => (event.target ? true : false)) : undefined}
        placeholdersNb={3}
        selectedEventId={selectedEventId}
        onSelectEvent={setSelectedEventId}
        onJoinEvent={onJoinEvent}
        kind="student"
        user={user}
      />
    </div>
  );
};
